<template>
  <div class="orders-grid" data-cy="orders-grid">
    <div v-if="loading" class="loader"></div>
    <div v-else data-cy="order-list-data">
      <div v-if="rowData.length > 0" class="form grid-cnt">
        <ag-grid-vue
          class="ag-theme-balham"
          :grid-options="gridOptions"
          :column-defs="columnDefinitions"
          :default-col-def="defaultColDef"
          :row-data="rowData"
          :modules="modules"
          :pagination="enablePagination"
          :pagination-page-size="pageSize"
          :suppress-menu-hide="true"
          :row-class-rules="rowClassRules"
          v-on:first-data-rendered="onFirstDataRendered"
          v-on:grid-ready="onGridReady">
        </ag-grid-vue>
      </div>
      <div v-else class="no-rows">
        {{ $t('viewOrders.no_orders_message') }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ordersMixins } from '@/mixins/orders.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import type { ActionDropdownItem } from '@/models/dropdown-item';
import { GridConfig } from '@/models/grid-config';
import { GridNames } from '@/models/grids-names.enum';
import { OrderOverviewModel } from '@/models/order-overview.model';
import { DraftsService } from '@/services/drafts.service';
import { OrderDocumentService } from '@/services/order-document-service';
import { OrdersService } from '@/services/orders.service';
import { useSelectedOrdersStore } from '@/stores/selected-grid-orders';
import { useUserStore } from '@/stores/user';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { GridApi, type GridOptions } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { DraftInitInfoDto, OrderHeaderModel, OrderStatus, OrderType } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent, type PropType } from 'vue';
import { useFormat } from '../../composables/format';
import { useGrid } from '../../composables/grid';
import { useLocale } from '../../composables/locale';
import { RouteNames } from '../../router';
import { useDraftsInitInfoStore } from '../../stores/drafts-init-info';
import { useGridsStore } from '../../stores/grids';
import OrdersGridActions from './orders-grid-actions.vue';
import OrdersGridSalesNumber from './orders-grid-sales-number.vue';

const VISIBLE = 1;
const shippedTotalQuantitiesDivider = ' / ';

export default defineComponent({
  name: 'orders-grid',
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    OrdersGridActions,
    // eslint-disable-next-line vue/no-unused-components
    OrdersGridSalesNumber,
  },
  props: {
    orderId: {
      type: String,
      default: undefined,
    },
    draftId: {
      type: String,
      default: undefined,
    },
    ordersParams: {
      type: Object,
      default: undefined,
    },
    enablePagination: {
      type: Boolean,
      default: true,
    },
    checkboxSelection: {
      type: Function as PropType<(node: any) => boolean>,
      default: () => false,
    },
    selectedActionItems: {
      type: Object as PropType<ActionDropdownItem>,
      default: undefined,
    },
  },
  setup() {
    const { selectedAccountId, selectedAccount } = storeToRefs(useUserStore());
    const { userLocale } = useLocale();
    const { addEditGridConfig, getGridByName } = useGridsStore();
    const { defaultGridOptions, dateColumnDefinition, applyGridConfig } = useGrid();
    const { formatDateNoUTC, formatCurrency } = useFormat();
    const { addSelectedOrders, removeSelectedOrders, getSelectedOrders } = useSelectedOrdersStore();
    const { getInitialInfoData } = useDraftsInitInfoStore();

    return {
      accountId: selectedAccountId,
      account: selectedAccount,
      userLocale,
      addEditGridConfig,
      getGridByName,
      defaultGridOptions,
      dateColumnDefinition,
      applyGridConfig,
      formatDateNoUTC,
      formatCurrency,
      addSelectedOrders,
      getSelectedOrders,
      removeSelectedOrders,
      getInitialInfoData,
    };
  },
  data() {
    return {
      loading: true,
      draftInitInfo: new DraftInitInfoDto(),
      ordersService: new OrdersService(),
      draftsService: new DraftsService(),
      orderDocumentService: new OrderDocumentService(),
      columnDefinitions: new Array<any>(),
      rowClassRules: undefined as any,
      orderOverview: new OrderOverviewModel(),
      defaultColDef: {
        resizable: true,
        filter: true,
        sortable: true,
        minWidth: 80,
      },
      rowData: new Array<any>(),
      gridName: GridNames.OrderHeaders,
      rowPixelsOffset: 1,
      pageSize: 50,
      gridApi: undefined as GridApi | undefined,
      modules: [ClientSideRowModelModule],
      initialGridConfig: new GridConfig() as Partial<GridConfig> | undefined,
      gridOptions: {
        ...this.defaultGridOptions(),
        getRowClass: (params: any) => {
          if (params.node.data.status === OrderStatus.NotSubmitted) {
            return 'draft-highlight';
          }
        },
        suppressPaginationPanel: !this.enablePagination,
        rowSelection: 'multiple',
        rowMultiSelectWithClick: false,
        suppressRowClickSelection: true,
        onFilterChanged: undefined as undefined | ((params: any) => void),
      } as GridOptions<any> as any,
    };
  },
  computed: {
    openOrderTypes(): OrderType[] {
      return this.draftInitInfo?.orderTypes?.map((orderType) => orderType.type);
    },
  },
  watch: {
    async accountId(newVal) {
      if (newVal) {
        this.draftInitInfo = await this.getInitialInfoData(newVal);
        this.fetchOrders();
      }
    },
    userLocale(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.gridApi?.refreshHeader();
          this.gridApi?.redrawRows();
        });
      }
    },
    selectedActionItems(newVal) {
      newVal ? this.updateSelectionColumn() : this.removeSelectionColumn();
    },
  },
  async mounted() {
    if (this.orderId) {
      this.$router.push({ name: RouteNames.ViewOrder, params: { id: this.orderId } });

      return;
    }

    if (this.draftId) {
      this.$router.push({ name: RouteNames.ViewOrder, params: { id: this.orderId } });

      return;
    }

    this.initialGridConfig = this.getGridByName(this.gridName);

    this.gridOptions.onFilterChanged = this.gridFilterEventHandler;
    this.gridOptions.onPaginationChanged = this.onPaginationChanged;
    this.gridOptions.onSortChanged = this.onSortChanged;
    this.gridOptions.onSelectionChanged = this.onSelectionChanged;

    await this.fetchDraftInitInfo();
    await this.fetchOrders();
  },
  beforeUnmount() {
    this.gridApi = undefined;
  },
  methods: {
    ...utilsMixins,
    ...ordersMixins,
    async fetchDraftInitInfo() {
      this.draftInitInfo = await this.getInitialInfoData(this.accountId);
    },
    isOrderDuplicatable(order: OrderHeaderModel) {
      return (
        (this.openOrderTypes?.includes(order.type) || order.type === OrderType.DropShip) &&
        order.type !== OrderType.LastSeason
      );
    },
    async fetchOrders() {
      this.loading = true;
      this.orderOverview = new OrderOverviewModel();

      const draftValidation = await this.ordersService.getOrderHeaders({
        ...this.ordersParams,
        accountId: this.accountId as string,
      });

      if (draftValidation.model) {
        this.rowData = draftValidation.model.map((order) => {
          order.dueDate = new Date(order.dueDate);

          if (order.date) {
            order.date = new Date(order.date);
          }

          order.duplicatable = this.isOrderDuplicatable(order);

          return { ...order, visible: VISIBLE };
        });
      } else {
        // TODO: show error
        return;
      }

      this.initializeGrid();

      window.addEventListener('resize', this.handleResize);

      this.loading = false;
      this.$emit('grid-loading-done');
    },
    onSelectionChanged() {
      const selectedRows = this.gridApi?.getSelectedRows() || [];
      this.addSelectedOrders(selectedRows);
      this.$emit('on-selection-changed', selectedRows);
    },
    onPaginationChanged(gridOptions: { api: GridApi }) {
      if (gridOptions.api) {
        const totalRows = gridOptions.api.paginationGetRowCount() || 0;

        this.$emit('update-row-count', totalRows);
        this.addEditGridConfig({
          name: this.gridName,
          pageNumber: gridOptions.api.paginationGetCurrentPage(),
        });
      }
    },
    onSortChanged() {
      const sortedColumn = this.gridApi?.getColumnState();

      if (sortedColumn) {
        this.addEditGridConfig({ name: this.gridName, sorting: sortedColumn });
      }
    },
    gridFilterEventHandler(params: { api: GridApi }) {
      const currentFilterModel = params.api.getFilterModel();

      if (currentFilterModel) {
        this.addEditGridConfig({ name: this.gridName, filters: currentFilterModel });
        this.setAndEmitOrderOverview(currentFilterModel, params);
      }
    },
    setAndEmitOrderOverview(currentFilterModel: any, params: any) {
      const reqDateFilterValues = currentFilterModel['dueDate'];

      if (!reqDateFilterValues) {
        this.orderOverview = new OrderOverviewModel();

        return;
      }

      const filteredResults = params.api?.rowModel?.rowsToDisplay;
      const orderNumbers: string[] = [];

      const totalItems = filteredResults?.reduce((itemCount: number, currentValue: any) => {
        if (this.isIncludedInOverview(currentValue.data)) {
          orderNumbers.push(currentValue.data.orderNumber);

          if (currentValue.data.quantitiesPerStatus) {
            return (
              itemCount +
              currentValue.data.quantitiesPerStatus.reduce(
                (accumulator: number, item: { quantity: number }) => accumulator + this.safeParseNumber(item.quantity),
                0,
              )
            );
          }

          return (
            itemCount +
            (this.safeParseNumber(currentValue.data.itemShipped) +
              this.safeParseNumber(currentValue.data.quantityToShip))
          );
        }

        return itemCount;
      }, 0);

      this.orderOverview = Object.assign(new OrderOverviewModel(), {
        totalItems,
        from: this.formatDateNoUTC(reqDateFilterValues.dateFrome),
        to: this.formatDateNoUTC(reqDateFilterValues.dateTo),
        filterType: reqDateFilterValues.type,
        orderNumbers,
      });

      this.$emit('grid-filter-update', this.orderOverview);
    },
    isIncludedInOverview(order: OrderHeaderModel): boolean {
      return this.hasOverviewInclusiveOrderType(order.type) && this.hasOverviewInclusiveStatus(order.status);
    },
    hasOverviewInclusiveOrderType(orderType: OrderType) {
      return [
        OrderType.PreOrder,
        OrderType.ReOrder,
        OrderType.DropShip,
        OrderType.MultiplePreOrder,
        OrderType.LastSeason,
      ].includes(orderType);
    },
    hasOverviewInclusiveStatus(status: string) {
      return [
        OrderStatus.Open,
        OrderStatus.Backorder,
        OrderStatus.PartiallyInvoiced,
        OrderStatus.PartiallyShipped,
        OrderStatus.Invoiced,
        OrderStatus.Delivered,
        OrderStatus.Hold,
        OrderStatus.UpdateInProgress,
      ].includes(status.toLowerCase() as OrderStatus);
    },
    calculateTotalOrderedItems(params: any) {
      {
        if (params.data.status === OrderStatus.NotSubmitted) {
          return '';
        }

        // for orders from dynamics
        if (params.data.quantitiesPerStatus) {
          return params.data.quantitiesPerStatus.reduce(
            (accumulator: number, item: { quantity: number }) => accumulator + this.safeParseNumber(item.quantity),
            0,
          );
        }

        // for orders from sales force
        return this.safeParseNumber(params.data.itemShipped) + this.safeParseNumber(params.data.quantityToShip);
      }
    },
    initializeGrid() {
      if (this.columnDefinitions.length === 0) {
        this.columnDefinitions = [
          {
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.sales_order_number'),
            width: 126,
            field: 'orderNumber',
            headerClass: 'order-number-header',
            suppressSizeToFit: true,
            cellRenderer: 'OrdersGridSalesNumber',
            tooltipField: 'country',
          },
          {
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.order_type'),
            headerClass: 'order-type-header',
            field: 'type',
            valueGetter: (params: any) => this.displayOrderType(params, 'type', this.$t),
          },
          {
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.order_status'),
            headerClass: 'order-status-header',
            field: 'status',
            cellClass: (params: any) => (params.value === OrderStatus.Hold ? 'hold' : null),
            valueGetter: (params: any) => this.$t(`viewOrders.order_status['${params.data.status.toLowerCase()}']`),
          },
          this.dateColumnDefinition({
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.order_date'),
            field: 'date',
            sort: 'desc',
          }),
          this.dateColumnDefinition({
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.ship_date'),
            field: 'dueDate',
          }),
          {
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.quantity_shipped'),
            field: 'itemShippedVsOrdered',
            width: 170,
            valueGetter: (params: any) => {
              if (this.isIncludedInOverview(params.data)) {
                return `${this.safeParseNumber(
                  params.data.itemShipped,
                )}${shippedTotalQuantitiesDivider}${this.calculateTotalOrderedItems(params)}`;
              } else if ([OrderType.WarrantyOrder, OrderType.ReturnOrder].includes(params.data.type)) {
                return `${this.calculateTotalOrderedItems(params)}`;
              }

              return '-';
            },
            comparator: (valueA: string, valueB: string) => {
              /**
               * Values are of the format shipped quantity / total quantity
               * Split the value by / and get the total quantity
               * Some values will have only total quanity, without the /
               * Hence, fetch the first numeric value from the far right to the left
               */

              const splittedQuantityA = valueA
                .split(shippedTotalQuantitiesDivider)
                .map((quantity) => parseInt(quantity, 10));
              const splittedQuantityB = valueB
                .split(shippedTotalQuantitiesDivider)
                .map((quantity) => parseInt(quantity, 10));

              const totalQuantityA = splittedQuantityA[1] || splittedQuantityA[0];
              const totalQuantityB = splittedQuantityB[1] || splittedQuantityB[0];

              if (isNaN(totalQuantityA) && isNaN(totalQuantityB)) {
                return 0;
              }

              if (isNaN(totalQuantityA)) {
                return -1;
              }

              if (isNaN(totalQuantityB)) {
                return 1;
              }

              return totalQuantityA - totalQuantityB;
            },
            cellStyle: { textAlign: 'right' },
          },
          // TODO Unhide after orders information in the database is corrected.
          // { headerValueGetter: () => this.$t('viewOrders.orders_list_columns.quantity_to_ship'),
          //   field: 'quantityToShip', valueFormatter: this.gridNumberFormatter },
          {
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.order_total'),
            field: 'total',
            valueFormatter: this.currencyFormatter,
            comparator: this.currencyComparator,
            cellStyle: { textAlign: 'right' },
          },
          {
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.customer_po_number'),
            field: 'customerPoNumber',
          },
          {
            headerValueGetter: () => this.$t('viewOrders.orders_list_columns.actions') as string,
            field: 'actions',
            suppressHeaderMenuButton: true,
            cellRenderer: 'OrdersGridActions',
          },
          { headerName: '', field: 'visible', hide: true, filter: 'agNumberColumnFilter' },
        ];
      }

      this.rowClassRules = {
        'gray-row': (params: any) => params.data.status === OrderStatus.Hold,
      };
    },
    restoreGridSelectionsFromStore() {
      const selectedOrders = this.getSelectedOrders();
      const selectedOrderNumbers = selectedOrders.map((order) => order.orderNumber);
      const nodesToSelect: any[] = [];
      const nodesToUnselect: any[] = [];

      this.gridApi?.forEachNode((node) => {
        selectedOrderNumbers.includes(node.data.orderNumber) ? nodesToSelect.push(node) : nodesToUnselect.push(node);
      });

      this.gridApi?.setNodesSelected({ nodes: nodesToSelect, newValue: true });
      this.gridApi?.setNodesSelected({ nodes: nodesToUnselect, newValue: false });

      this.updateSelectionColumn();
    },
    updateSelectionColumn() {
      const selectionColumn = {
        headerValueGetter: () => '',
        field: 'checkbox',
        checkboxSelection: this.checkboxSelection,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        filter: false,
        minWidth: 35,
        width: 35,
      };

      const existingIndex = this.columnDefinitions.findIndex((col) => col.field === 'checkbox');
      if (existingIndex !== -1) {
        this.columnDefinitions[existingIndex] = selectionColumn;
      } else {
        this.columnDefinitions.unshift(selectionColumn);
      }

      this.refreshGrid();
    },
    removeSelectionColumn() {
      this.columnDefinitions = this.columnDefinitions.filter((col) => col.field !== 'checkbox');
      this.gridApi?.deselectAll();
      this.removeSelectedOrders();
      this.onFirstDataRendered();
    },
    refreshGrid() {
      if (this.gridApi) {
        this.gridApi.updateGridOptions({
          columnDefs: this.columnDefinitions,
          isRowSelectable: this.checkboxSelection,
        });
        this.gridApi.redrawRows();
        this.gridApi.refreshHeader();
        this.onFirstDataRendered();
      }
    },
    onGridReady(params: any) {
      this.gridApi = params.api;

      if (this.initialGridConfig) {
        this.applyGridConfig(this.initialGridConfig, this.gridApi as GridApi);
      }

      if (this.selectedActionItems) {
        this.restoreGridSelectionsFromStore();
      }
    },
    onFirstDataRendered() {
      this.gridApi?.sizeColumnsToFit();
    },
    handleResize() {
      this.gridApi?.sizeColumnsToFit();
    },
    currencyFormatter(params: any) {
      return this.formatCurrency(params.data.currencyCode, params.value);
    },
    currencyComparator(a: number, b: number) {
      // We want currencies to be sorted by value, disregarding
      // currency. The `|| 0` is for undefined and NaN values, of which
      // for some reason we have some.
      return (a || 0) - (b || 0);
    },
    removeAllGridFilters() {
      this.gridApi?.setFilterModel(null);
    },
  },
});
</script>
<style scoped lang="scss">
.orders-grid:deep() {
  .draft-highlight {
    background: var(--row-highlight-color) !important;
  }

  .hold {
    color: var(--special-red-color-darker);
  }

  .gray-row {
    color: var(--light-gray-color);
  }

  .ag-theme-balham {
    width: 100%;
    height: 100%;
    --ag-selected-row-background-color: transparent !important;
  }

  .grid-cnt {
    padding: 0;
    border-top: 1px var(--gray-color)-border solid;
    border-bottom: 1px var(--gray-color)-border solid;
  }

  .no-rows {
    padding-bottom: 20px;
  }
}
</style>
